// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.
/**
 * Plans an import (PLAN.md P3-T08): an Obsidian vault, a Notion export, or a folder of
 * Markdown, from a folder or a zip (the app reads those; this works on the files). Paths are
 * cleaned up to the vault rules; Notion's page IDs are taken out of names and its links are
 * rewritten to match, so links stay intact. Only notes (.md) and canvases (.canvas) are
 * imported; attachments, CSV files, and Obsidian's `.obsidian/` settings are listed as skipped.
 *
 * Every plan imports. What is imported must meet these conditions (IMPORT_RULES); anything
 * else is listed as skipped with the reason: only notes and canvases, notes are UTF-8 text, each file fits the
 * plan's file size limit, names are allowed in a vault, and system or trash files are left out.
 */
import { dirname, extname, fileKind, pathKey, tryNormalizePath } from "./path.js";
/** The reasons a file is skipped, in the order they are checked. */
export const IMPORT_RULES = {
    system: "not synced (system or trash file)",
    type: "not a note (.md) or canvas (.canvas)",
    name: "name not allowed in a vault",
    encoding: "not a UTF-8 text note",
    size: "larger than your plan's file size limit",
};
const NOTION_ID = / [0-9a-f]{32}$/;
const SKIP = /(^|\/)(__MACOSX|\.git|\.trash)(\/|$)|(^|\/)(\.DS_Store|Thumbs\.db|desktop\.ini)$|(^|\/)~\$/i;
/** System and trash files, which an import always leaves out: no need to read them. */
export function isSkippedImportPath(path) {
    return SKIP.test(path.replace(/\\/gu, "/"));
}
/** Only notes (.md) and canvases (.canvas) are imported. */
export function isImportablePath(path) {
    return /\.(md|canvas)$/iu.test(path);
}
const decoder = new TextDecoder();
const strictDecoder = new TextDecoder("utf-8", { fatal: true });
function isUtf8(data) {
    try {
        strictDecoder.decode(data);
        return true;
    }
    catch {
        return false;
    }
}
const encoder = new TextEncoder();
/** Removes a folder that wraps everything (a zip of "My Vault/..."). */
function stripWrapper(paths) {
    const first = paths[0]?.split("/")[0];
    if (!first || first === ".obsidian")
        return "";
    return paths.every((p) => p.startsWith(`${first}/`)) ? `${first}/` : "";
}
export function detectSource(paths) {
    if (paths.some((p) => p === ".obsidian" || p.startsWith(".obsidian/") || p.includes("/.obsidian/")))
        return "obsidian";
    const ids = paths.filter((p) => p.split("/").some((seg) => NOTION_ID.test(seg.slice(0, seg.length - extname(seg).length)))).length;
    if (ids > 0 && ids >= paths.length / 2)
        return "notion";
    return "markdown";
}
/** "Page 0123…cdef.md" → "Page.md"; "Folder 0123…cdef" → "Folder". */
function stripNotionId(segment) {
    const ext = extname(segment);
    const stem = segment.slice(0, segment.length - ext.length);
    const clean = stem.replace(NOTION_ID, "");
    return (clean || "Untitled") + ext;
}
/** Characters the path rules forbid become spaces or dashes; names cannot end in "." or " ". */
function cleanSegment(seg) {
    // Control characters are removed on purpose (Appendix B.2 rule 4).
    // eslint-disable-next-line no-control-regex
    let s = seg.replace(/[\\:*?"<>|\u0000-\u001f\u007f]/gu, "-").trim();
    s = s.replace(/[. ]+$/u, "");
    return s || "Untitled";
}
function cleanPath(p) {
    return p.split("/").filter(Boolean).map(cleanSegment).join("/");
}
function relative(fromDir, to) {
    const a = fromDir ? fromDir.split("/") : [];
    const b = to.split("/");
    let i = 0;
    while (i < a.length && i < b.length - 1 && a[i] === b[i])
        i++;
    return [...Array(a.length - i).fill(".."), ...b.slice(i)].join("/");
}
function joinRelative(dir, rel) {
    const out = dir ? dir.split("/") : [];
    for (const seg of rel.split("/")) {
        if (seg === "..")
            out.pop();
        else if (seg !== "." && seg !== "")
            out.push(seg);
    }
    return out.join("/");
}
/** Rewrites Markdown links that point at renamed files (Notion exports). */
function rewriteLinks(text, fromOld, fromNew, renamed) {
    return text.replace(/\]\(([^)\s]+)(\s+"[^"]*")?\)/gu, (whole, url, title) => {
        if (/^[a-z][a-z0-9+.-]*:/iu.test(url) || url.startsWith("#"))
            return whole;
        const hash = url.indexOf("#");
        const target = hash >= 0 ? url.slice(0, hash) : url;
        const frag = hash >= 0 ? url.slice(hash) : "";
        let decoded;
        try {
            decoded = decodeURIComponent(target);
        }
        catch {
            return whole;
        }
        const resolved = joinRelative(dirname(fromOld), decoded);
        const to = renamed.get(resolved);
        if (to === undefined)
            return whole;
        const rel = relative(dirname(fromNew), to);
        return `](${encodeURI(rel)}${frag}${title ?? ""})`;
    });
}
export function planImport(entries, o = {}) {
    const skipped = [];
    const raw = entries
        .map((e) => ({ path: e.path.replace(/\\/gu, "/").replace(/^\/+/u, ""), data: e.data, size: e.size ?? e.data.length }))
        .filter((e) => {
        if (e.path === "" || e.path.endsWith("/"))
            return false;
        if (SKIP.test(e.path)) {
            skipped.push({ path: e.path, reason: IMPORT_RULES.system });
            return false;
        }
        return true;
    });
    const wrapper = stripWrapper(raw.map((e) => e.path));
    const all = raw.map((e) => ({ path: e.path.slice(wrapper.length), data: e.data, size: e.size }));
    // The source shows in files that are not imported (Obsidian's .obsidian/ folder).
    const source = detectSource(all.map((f) => f.path));
    const files = all.filter((f) => {
        if (isImportablePath(f.path))
            return true;
        skipped.push({ path: f.path, reason: IMPORT_RULES.type });
        return false;
    });
    // New names: Notion IDs removed, forbidden characters replaced, then de-duplicated.
    const prefix = o.folder ? `${cleanPath(o.folder)}/` : "";
    const taken = new Set();
    const renamed = new Map();
    for (const f of files) {
        let p = source === "notion" ? f.path.split("/").map(stripNotionId).join("/") : f.path;
        p = prefix + cleanPath(p);
        const ext = extname(p);
        const stem = p.slice(0, p.length - ext.length);
        for (let n = 2; taken.has(pathKey(p)) || o.exists?.(p); n++)
            p = `${stem} ${n}${ext}`;
        const normalized = tryNormalizePath(p);
        if (normalized === null) {
            skipped.push({ path: f.path, reason: IMPORT_RULES.name });
            continue;
        }
        taken.add(pathKey(normalized));
        renamed.set(f.path, normalized);
    }
    const out = [];
    for (const f of files) {
        const to = renamed.get(f.path);
        if (to === undefined)
            continue;
        if (o.maxFileBytes !== undefined && f.size > o.maxFileBytes) {
            skipped.push({ path: f.path, reason: IMPORT_RULES.size });
            continue;
        }
        if (fileKind(to) === "markdown" && !isUtf8(f.data)) {
            skipped.push({ path: f.path, reason: IMPORT_RULES.encoding });
            continue;
        }
        let data = f.data;
        if (source === "notion" && fileKind(to) === "markdown") {
            const text = decoder.decode(f.data);
            const rewritten = rewriteLinks(text, f.path, to, renamed);
            if (rewritten !== text)
                data = encoder.encode(rewritten);
        }
        out.push({ path: to, data });
    }
    out.sort((a, b) => a.path.localeCompare(b.path));
    return { source, files: out, skipped };
}
/** A short description of a plan for the user ("12 notes, 2 canvases"). */
export function summarizeImport(plan) {
    let notes = 0;
    let canvases = 0;
    for (const f of plan.files) {
        if (fileKind(f.path) === "markdown")
            notes++;
        else
            canvases++;
    }
    return { notes, canvases };
}
//# sourceMappingURL=import.js.map